// Toplu yeniden hazirlama (IP temizligi): secilen pasif listeler icin Dengeli tasarim uretilir,
// burada ilham / eski / yeni yan yana onaylanir, onaylananlar Etsy'de guncellenip geri acilir.
import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, RefreshCw, SkipForward, Wand2, X } from "lucide-react";
import { authHeaders } from "../../lib/http";
import { useDesignModel } from "../../lib/useDesignModel";

const API_BASE = import.meta.env.VITE_API_URL || "";
const img = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);
const headers = () => ({ ...authHeaders(), "Content-Type": "application/json" });

type Rework = {
  status: "generating" | "ready" | "failed" | "approved" | "skipped" | "applying" | "done" | "error";
  at: string; sku?: string; url?: string; insp?: string; old?: string; error?: string;
  risk?: { score: number; reason: string } | null;
};
type Item = { id: string; title: string; etsyListingId: string | null; sales: number | null; favorites: number | null; views: number | null; rework: Rework };
type Job = { kind: "generate" | "apply"; running: boolean; total: number; done: number; errors: number } | null;

const STATUS: Record<Rework["status"], { label: string; cls: string }> = {
  generating: { label: "Üretiliyor", cls: "bg-indigo-50 text-indigo-700" },
  ready: { label: "Onay bekliyor", cls: "bg-amber-50 text-amber-700" },
  failed: { label: "Üretilemedi", cls: "bg-red-50 text-red-600" },
  approved: { label: "Onaylandı", cls: "bg-emerald-50 text-emerald-700" },
  skipped: { label: "Atlandı", cls: "bg-gray-100 text-gray-600" },
  applying: { label: "Uygulanıyor", cls: "bg-indigo-50 text-indigo-700" },
  done: { label: "Etsy'de açıldı", cls: "bg-emerald-50 text-emerald-700" },
  error: { label: "Hata", cls: "bg-red-50 text-red-600" },
};
// Kopya yargici puani: 0 farkli - 10 birebir
const riskCls = (s: number) => (s >= 6 ? "bg-red-50 text-red-600" : s >= 4 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700");

function Pic({ src, label }: { src?: string; label: string }) {
  return (
    <figure className="w-28 shrink-0">
      <div className="bg-alpha-check aspect-square overflow-hidden rounded-lg border border-gray-200">
        {src ? <img src={img(src)} alt={label} className="h-full w-full object-contain" loading="lazy" /> : null}
      </div>
      <figcaption className="mt-1 text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</figcaption>
    </figure>
  );
}

export function ReworkModal({ startIds, onClose, onChanged }: { startIds?: string[]; onClose: () => void; onChanged: () => void }) {
  const { models, model, setModel } = useDesignModel("erc:model:design-workshop");
  const [note, setNote] = useState("");
  const [starting, setStarting] = useState(!!startIds?.length);
  const [items, setItems] = useState<Item[]>([]);
  const [job, setJob] = useState<Job>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/listing-rework`, { headers: headers() }).catch(() => null);
    const j = r?.ok ? await r.json().catch(() => null) : null;
    if (j?.data) { setItems(j.data.items); setJob(j.data.job); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  // Uretim/uygulama surerken izle
  const live = job?.running || items.some((i) => i.rework.status === "generating" || i.rework.status === "applying");
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [live, load]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function post(path: string, body: unknown) {
    setErr(null);
    const r = await fetch(`${API_BASE}/api/listing-rework/${path}`, { method: "POST", headers: headers(), body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(j.error || "İşlem başarısız"); return false; }
    await load();
    return true;
  }
  async function generate(ids: string[], withNote?: string) {
    setBusy(true);
    try { if (await post("generate", { ids, model, note: withNote || undefined })) setStarting(false); } finally { setBusy(false); }
  }
  const decide = (ids: string[], decision: "approved" | "skipped" | "clear") => post("decide", { ids, decision });
  async function apply() {
    const n = items.filter((i) => i.rework.status === "approved").length;
    if (!confirm(`${n} liste yeni tasarımla Etsy'de güncellenip yeniden AÇILACAK (görseller, başlık, etiketler). Devam edilsin mi?`)) return;
    setBusy(true);
    try { await post("apply", {}); onChanged(); } finally { setBusy(false); }
  }

  const count = (s: Rework["status"]) => items.filter((i) => i.rework.status === s).length;
  const lowRisk = items.filter((i) => i.rework.status === "ready" && i.rework.risk && i.rework.risk.score <= 3).map((i) => i.id);
  const finished = items.filter((i) => ["done", "skipped"].includes(i.rework.status)).map((i) => i.id);
  // Siralama: karar bekleyenler once, sonra uretilenler, en sonda bitenler
  const order: Rework["status"][] = ["ready", "error", "failed", "approved", "generating", "applying", "skipped", "done"];
  const shown = [...items].sort((a, b) => order.indexOf(a.rework.status) - order.indexOf(b.rework.status));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Toplu yeniden hazırlama</h3>
            <p className="text-xs text-gray-500">İlham, eski ve yeni tasarım yan yana. Onaylananlar yeni mockup, başlık ve etiketlerle Etsy'de geri açılır.</p>
          </div>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        {starting && startIds && (
          <div className="space-y-3 border-b border-gray-200 p-5">
            <p className="text-sm text-gray-700"><b>{startIds.length}</b> liste için ilham analizinden <b>Dengeli</b> tasarım üretilecek (aynı anda 3, arka planda; sayfayı kapatabilirsin).</p>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-medium text-gray-700">Model
                <select value={model} onChange={(e) => setModel(e.target.value)}
                  className="mt-1 block rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200">
                  {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
              <label className="min-w-[240px] flex-1 text-xs font-medium text-gray-700">Ek not (isteğe bağlı, hepsine eklenir)
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ör. daha sade, tek renk baskıya uygun"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
              </label>
              <button onClick={() => void generate(startIds, note)} disabled={busy || !model}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Üret
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-5 py-3 text-xs">
          {job?.running && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-0.5 font-medium text-indigo-700">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {job.kind === "generate" ? "Üretiliyor" : "Etsy'ye uygulanıyor"} {job.done}/{job.total}
            </span>
          )}
          <span className="text-gray-500">{count("ready")} onay bekliyor · {count("approved")} onaylı · {count("done")} açıldı</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {lowRisk.length > 0 && (
              <button onClick={() => void decide(lowRisk, "approved")} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50">
                Düşük riskli {lowRisk.length} tasarımı onayla
              </button>
            )}
            {finished.length > 0 && (
              <button onClick={() => void decide(finished, "clear")} title="Bitenleri ve atlananları listeden kaldır" className="rounded-lg px-3 py-1.5 text-gray-600 hover:bg-gray-100">Bitenleri temizle</button>
            )}
            <button onClick={() => void apply()} disabled={busy || !!job?.running || count("approved") === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
              <Check className="h-4 w-4" /> Onaylananları uygula ({count("approved")})
            </button>
          </div>
        </div>
        {err && <p className="mx-5 mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}

        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {shown.length === 0 && !starting && <p className="py-10 text-center text-sm text-gray-500">Yeniden hazırlanan liste yok. Listeleri seçip Toplu işlem → "Yeni tasarım üret" ile başla.</p>}
          {shown.map((it) => {
            const rw = it.rework, st = STATUS[rw.status];
            const canDecide = !!rw.url && ["ready", "error", "skipped", "approved"].includes(rw.status);
            return (
              <div key={it.id} className="flex flex-wrap items-start gap-3 rounded-xl border border-gray-200 p-3">
                <Pic src={rw.insp} label="İlham" />
                <Pic src={rw.old} label="Eski" />
                <div className="relative">
                  <Pic src={rw.url} label="Yeni" />
                  {rw.status === "generating" && <span className="absolute inset-0 flex items-center justify-center pb-5"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></span>}
                </div>
                <div className="min-w-[200px] flex-1 space-y-1.5">
                  <p className="line-clamp-2 text-sm font-medium text-gray-900">{it.title}</p>
                  <p className="text-xs text-gray-500">#{it.etsyListingId} · {it.sales ?? 0} satış · {it.favorites ?? 0} favori{rw.sku ? ` · ${rw.sku}` : ""}</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                    {rw.risk && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskCls(rw.risk.score)}`} title={rw.risk.reason}>Kopya riski {rw.risk.score}/10</span>}
                  </div>
                  {rw.risk?.reason && <p className="text-xs text-gray-500">{rw.risk.reason}</p>}
                  {rw.error && <p className="text-xs text-red-600">{rw.error}</p>}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {canDecide && rw.status !== "approved" && (
                      <button onClick={() => void decide([it.id], "approved")} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"><Check className="h-3.5 w-3.5" /> Onayla</button>
                    )}
                    {!["generating", "applying", "done"].includes(rw.status) && (
                      <button onClick={() => { const n = prompt("Yeniden üretim için not (isteğe bağlı):", "") ?? null; if (n !== null) void generate([it.id], n); }} disabled={busy || !!job?.running}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"><RefreshCw className="h-3.5 w-3.5" /> Yeniden üret</button>
                    )}
                    {!["skipped", "applying", "done"].includes(rw.status) && (
                      <button onClick={() => void decide([it.id], "skipped")} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100"><SkipForward className="h-3.5 w-3.5" /> Atla</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
